import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService, organizationService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { isProductionMode } from '../services/productionModeService';
import { toast } from 'react-hot-toast';

export const LoginPage: React.FC = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // KYC & Category Fields
  const [accountCategory, setAccountCategory] = useState<'individual' | 'company' | 'staff'>('individual');
  const [requestedRole, setRequestedRole] = useState('technician');
  
  // Individual & Staff Fields
  const [idType, setIdType] = useState('NIN');
  const [idNumber, setIdNumber] = useState('');
  const [idCardFile, setIdCardFile] = useState<File | null>(null);

  // Company Fields
  const [companyName, setCompanyName] = useState('');
  const [cacFile, setCacFile] = useState<File | null>(null);
  const [memartFile, setMemartFile] = useState<File | null>(null);
  const [applicationFile, setApplicationFile] = useState<File | null>(null);

  const [isVerifying, setIsVerifying] = useState(false);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // The AuthContext automatically handles fetching the user document and setting state
  // via onAuthStateChanged, so we just need to wait for authentication to succeed.
  const { isAuthenticated } = useAuth();

  // Handle URL query parameters for direct links (e.g. ?type=staff or ?role=staff)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type') || params.get('role') || params.get('ref');
    if (typeParam === 'staff' || typeParam === 'employee') {
      setIsRegistering(true);
      setAccountCategory('staff');
      setRequestedRole('field_staff');
    } else if (typeParam === 'company' || typeParam === 'tenant') {
      setIsRegistering(true);
      setAccountCategory('company');
    } else if (typeParam === 'technician' || typeParam === 'owner') {
      setIsRegistering(true);
      setAccountCategory('individual');
      setRequestedRole(typeParam);
    }
  }, [location.search]);

  // If already authenticated, redirect to previous target or /dashboard
  useEffect(() => {
    if (isAuthenticated) {
      const fromState = (location.state as any)?.from;
      const from = fromState 
        ? `${fromState.pathname || ''}${fromState.search || ''}`
        : '/dashboard';
      navigate(from || '/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegistering) {
        setIsVerifying(true);
        toast.loading('Submitting registration...');

        // Helper function for uploading to Cloudinary
        const uploadFile = async (file: File) => {
          const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/YOUR_CLOUD_NAME_HERE/image/upload';
          const CLOUDINARY_UPLOAD_PRESET = 'YOUR_UPLOAD_PRESET_HERE'; // Must be an 'unsigned' preset
          const formData = new FormData();
          formData.append('file', file);
          formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
          
          try {
            const uploadRes = await fetch(CLOUDINARY_URL, { method: 'POST', body: formData });
            const data = await uploadRes.json();
            return data.secure_url || null;
          } catch (err) {
            console.error('Upload failed', err);
            return null;
          }
        };

        let registeredUserId = null;

        if (accountCategory === 'individual') {
          let uploadedUrl = null;
          if (idCardFile) {
            uploadedUrl = await uploadFile(idCardFile);
          }
          const res = await authService.register(name, email, password, requestedRole, idType, idNumber, uploadedUrl || undefined, 'individual');
          toast.success('🎉 Welcome to Verisure! Your Free account with 5 QR Codes is ready.');
          navigate('/dashboard');
        } else if (accountCategory === 'company') {
          // Company Flow
          let cacUrl, memartUrl, applicationUrl;
          if (cacFile) cacUrl = await uploadFile(cacFile);
          if (memartFile) memartUrl = await uploadFile(memartFile);
          if (applicationFile) applicationUrl = await uploadFile(applicationFile);

          // Create the admin user for the company on free plan
          const res = await authService.register(name, email, password, 'master_admin', undefined, undefined, undefined, 'company');
          registeredUserId = res.data.userId;

          if (registeredUserId) {
            await organizationService.registerOrganization(companyName, registeredUserId, { cacUrl, memartUrl, applicationUrl });
            toast.success('🎉 Organization account created! 10 Free QR Codes are active.');
            navigate('/dashboard');
          }
        } else {
          // Staff Flow
          let uploadedUrl = null;
          if (idCardFile) {
            uploadedUrl = await uploadFile(idCardFile);
          }
          await authService.register(name, email, password, requestedRole, idType, idNumber, uploadedUrl || undefined, 'staff');
          toast.success('Staff onboarding submitted! Please await Super Admin approval.');
          navigate('/pending');
        }
        
        setIsVerifying(false);
      } else {
        await authService.login(email, password);
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
      setIsVerifying(false);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error('Please enter your email address first.');
      return;
    }
    try {
      setLoading(true);
      await authService.resetPassword(email);
      toast.success('Password reset email sent! Check your inbox.', { icon: '📧' });
    } catch (err: any) {
      toast.error(err.message || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4 font-sans">
      <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl p-6 md:p-8 mx-auto text-slate-100">
        <div className="flex flex-col items-center justify-center mb-6">
          <img src="/official.png" alt="Verisure" className="h-16 w-auto object-contain mb-2" />
          <p className="text-[10px] text-orange-400 font-bold tracking-[2px] uppercase">Secure. Assured. Always.</p>
        </div>
        <h2 className="text-xl font-bold mb-6 text-center text-white">
          {isRegistering 
            ? (accountCategory === 'staff' ? 'Staff Onboarding Portal' : 'Create Certified Account') 
            : 'Welcome Back'}
        </h2>
        
        {location.state?.from && (
          <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/30 text-orange-300 rounded-2xl text-xs flex items-center gap-2.5">
            <svg className="w-4 h-4 shrink-0 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Authentication required. Please sign in to access your requested page.</span>
          </div>
        )}

        {error && <div className="mb-4 p-3 bg-red-950/70 border border-red-800 text-red-300 rounded-xl text-sm">{error}</div>}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegistering && (
            <>
              {/* Freemium Highlight Banner */}
              <div className="p-3.5 bg-gradient-to-r from-orange-500/15 via-red-500/10 to-amber-500/15 border border-orange-500/30 rounded-2xl text-xs space-y-1">
                <div className="flex items-center gap-2 text-orange-400 font-black uppercase tracking-wider text-[11px]">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span>100% Free Freemium Access</span>
                </div>
                <p className="text-slate-300 text-[11px] leading-relaxed">
                  Register without paying a dime! Individuals get <strong>5 Free QR Codes</strong> and Organizations get <strong>10 Free QR Codes</strong>. Unlimited with Premium.
                </p>
              </div>

              {/* Category Selector with Staff Onboarding Option */}
              <div className="flex bg-slate-950 p-1 rounded-xl mb-4 border border-slate-800 gap-1">
                <button
                  type="button"
                  onClick={() => { setAccountCategory('individual'); setRequestedRole('owner'); }}
                  className={`flex-1 py-2 text-xs font-bold rounded-lg transition ${accountCategory === 'individual' ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Individual
                </button>
                <button
                  type="button"
                  onClick={() => setAccountCategory('company')}
                  className={`flex-1 py-2 text-xs font-bold rounded-lg transition ${accountCategory === 'company' ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Company
                </button>
                <button
                  type="button"
                  onClick={() => { setAccountCategory('staff'); setRequestedRole('field_staff'); }}
                  className={`flex-1 py-2 text-xs font-bold rounded-lg transition ${accountCategory === 'staff' ? 'bg-gradient-to-r from-blue-600 to-indigo-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Staff / Team
                </button>
              </div>

              {accountCategory === 'individual' ? (
                <>
                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">Account Type</label>
                    <select
                      value={requestedRole}
                      onChange={(e) => setRequestedRole(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 text-white rounded-xl focus:outline-none focus:border-orange-500"
                    >
                      <option value="owner">Equipment Owner (5 Free QR Codes)</option>
                      <option value="technician">Certified Technician (Field Service)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">Government ID Type</label>
                    <select
                      value={idType}
                      onChange={(e) => setIdType(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 text-white rounded-xl focus:outline-none focus:border-orange-500"
                    >
                      <option value="NIN">National Identity Number (NIN)</option>
                      <option value="Passport">Passport Number</option>
                      <option value="ID_Card">National ID Card</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">ID Number (Optional)</label>
                    <input
                      type="text"
                      value={idNumber}
                      onChange={(e) => setIdNumber(e.target.value)}
                      placeholder="Enter ID number for verification"
                      className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 text-white rounded-xl focus:outline-none focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">
                      Upload ID Card Photo {requestedRole === 'technician' ? '(Required for Techs)' : '(Optional)'}
                    </label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setIdCardFile(e.target.files ? e.target.files[0] : null)}
                      className="w-full px-4 py-2 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500 bg-slate-950 text-slate-300 text-xs"
                      required={requestedRole === 'technician'}
                    />
                    <p className="text-xs text-slate-500 mt-1">Upload ID photo for KYC verification badge.</p>
                  </div>
                </>
              ) : accountCategory === 'staff' ? (
                <>
                  <div className="p-3 bg-blue-950/40 border border-blue-800/50 rounded-xl text-xs text-blue-300 mb-2">
                    <p className="font-semibold">🏢 Verisure Staff Onboarding</p>
                    <p className="text-slate-400 mt-0.5">Register as a staff member. Your operational role and permissions will be assigned by the Super Admin upon approval.</p>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">Account Type</label>
                    <input
                      type="text"
                      value="Verisure Staff Member"
                      disabled
                      className="w-full px-4 py-2.5 bg-slate-950/70 border border-slate-700 text-blue-300 font-semibold rounded-xl cursor-not-allowed text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">Identification Type</label>
                    <select
                      value={idType}
                      onChange={(e) => setIdType(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 text-white rounded-xl focus:outline-none focus:border-blue-500"
                    >
                      <option value="Staff_ID">Employee / Staff ID Card</option>
                      <option value="NIN">National Identity Number (NIN)</option>
                      <option value="Passport">Passport Number</option>
                      <option value="ID_Card">National ID Card</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">Staff / Gov ID Number</label>
                    <input
                      type="text"
                      value={idNumber}
                      onChange={(e) => setIdNumber(e.target.value)}
                      placeholder="e.g. VRS-STF-2026 or NIN"
                      className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 text-white rounded-xl focus:outline-none focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">Upload Staff / Gov ID Document</label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setIdCardFile(e.target.files ? e.target.files[0] : null)}
                      className="w-full px-4 py-2 border border-slate-700 rounded-xl focus:outline-none focus:border-blue-500 bg-slate-950 text-slate-300 text-xs"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">Upload a photo of your staff badge or government ID.</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">Company Name</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="e.g. Verisure Tech Ltd"
                      className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 text-white rounded-xl focus:outline-none focus:border-orange-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">CAC Document</label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setCacFile(e.target.files ? e.target.files[0] : null)}
                      className="w-full px-4 py-2 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500 bg-slate-950 text-slate-300 text-xs"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">MEMART Document</label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setMemartFile(e.target.files ? e.target.files[0] : null)}
                      className="w-full px-4 py-2 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500 bg-slate-950 text-slate-300 text-xs"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">Application Document</label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => setApplicationFile(e.target.files ? e.target.files[0] : null)}
                      className="w-full px-4 py-2 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500 bg-slate-950 text-slate-300 text-xs"
                      required
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">
                  {accountCategory === 'company' ? 'Admin Full Name' : 'Full Name'}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 text-white rounded-xl focus:outline-none focus:border-orange-500"
                  required
                />
              </div>
            </>
          )}
          
          <div>
            <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider mb-2">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 text-white rounded-xl focus:outline-none focus:border-orange-500"
              required
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-slate-300 text-xs font-bold uppercase tracking-wider">Password</label>
              {!isRegistering && (
                <button 
                  type="button" 
                  onClick={handleForgotPassword} 
                  disabled={loading}
                  className="text-xs text-orange-400 hover:text-orange-300 focus:outline-none disabled:opacity-50 font-bold"
                >
                  Forgot Password?
                </button>
              )}
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-700 text-white rounded-xl focus:outline-none focus:border-orange-500 pr-10"
                required
                minLength={6}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-200 focus:outline-none select-none"
                onMouseDown={() => setShowPassword(true)}
                onMouseUp={() => setShowPassword(false)}
                onMouseLeave={() => setShowPassword(false)}
                onTouchStart={() => setShowPassword(true)}
                onTouchEnd={() => setShowPassword(false)}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                )}
              </button>
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-orange-500/25 hover:opacity-95 disabled:opacity-50 transition"
          >
            {loading ? (isVerifying ? 'Submitting...' : 'Processing...') : (isRegistering ? (accountCategory === 'staff' ? 'Submit Staff Registration' : 'Create Certified Account') : 'Sign In')}
          </button>

          {!isProductionMode() && !isRegistering && (
            <button
              type="button"
              onClick={() => {
                const demoAdmin = {
                  id: 'demo-super-admin',
                  name: 'Verisure Super Admin',
                  email: 'admin@verisure.com',
                  role: 'super_admin',
                  organizationId: 'demo-org',
                  isActive: true,
                  createdAt: new Date(),
                };
                localStorage.setItem('demo_auth_user', JSON.stringify(demoAdmin));
                toast.success('Logged in as Super Admin (Sandbox Mode)');
                navigate('/admin/blank-qr');
              }}
              className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-orange-400 border border-orange-500/30 font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-2 transition shadow-sm"
            >
              <span>⚡</span> Quick Demo Super Admin Login (Dev Mode)
            </button>
          )}
        </form>

        <div className="mt-6 text-center text-sm space-y-2">
          <div>
            <button 
              onClick={() => setIsRegistering(!isRegistering)} 
              className="text-orange-400 hover:text-orange-300 font-bold transition focus:outline-none"
            >
              {isRegistering 
                ? 'Already have an account? Sign In' 
                : 'Need an account? Create Account'}
            </button>
          </div>

          {!isRegistering && (
            <div>
              <button 
                onClick={() => { setIsRegistering(true); setAccountCategory('staff'); setRequestedRole('field_staff'); }} 
                className="text-xs text-blue-400 hover:text-blue-300 font-semibold transition focus:outline-none flex items-center justify-center gap-1.5 mx-auto"
              >
                <span>🏢</span>
                <span>Joining the Verisure Team? Staff Registration Link</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
